import React, {useMemo} from "react";
import {css, useTheme} from "@emotion/react";
import {parse} from "date-fns";

import {Button} from "@pg-design/button-module";
import {borderRadius, display, flexJustifyCenter, mb, mt, mv, p} from "@pg-design/helpers-css";
import {Link} from "@pg-design/link-module";
import {Highlight, Text} from "@pg-design/text-module";
import {appendQueryString} from "@pg-mono/request";
import {formatPL, phoneNumberFormat} from "@pg-mono/string-utils";

import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {Country} from "../../region/types/Country";
import {offerViewExternalSiteClick, propertyViewExternalSiteClick} from "../../tracking/algolytics_hits/external_site_click";
import {generateHash} from "../../utils/generate_hash";
import {InquiryButtonTypeName} from "../detail/constants/inquiry_button_type";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferAssistant} from "../types/IOfferAssistant";
import {IOfferTrader} from "../types/IOfferTrader";
import {ISalesOffice} from "../types/ISalesOffice";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";

interface IProps {
    offer: {
        assistant: IOfferAssistant | null;
        configuration: {
            include_utm: boolean;
            phone_clip: string | null;
            display_type: OfferDisplayType;
            limited_presentation?: boolean;
            is_autoresponder_enabled: boolean;
        };
        geo_point: {
            coordinates: [number, number];
        };
        id: number;
        is_phone_clip_enabled: boolean;
        logo: {
            o_log_200: string;
        } | null;
        name: string;
        region: {
            country: Country;
            full_name: string;
            id: number;
        };
        sales_offices: ISalesOffice[];
        traders: IOfferTrader[];
        type: number;
        vendor: {
            id: number;
            name: string;
            slug: string;
            logo: {
                v_log_160x120: string;
            } | null;
        };
        website: string | null;
        is_active?: boolean;
    };
    property?: {
        area: number;
        discount: number;
        floor: number;
        floors: number;
        id: number;
        is_luxury: boolean;
        is_reserved: boolean;
        mdm: Object | null;
        number: string;
        plan: string | null;
        rooms: number;
    };
}

export const OfferSalesOffices = (props: IProps) => {
    const {offer, property} = props;
    const {vendor} = props.offer;
    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const hideForm = offer.sales_offices.length === 0;

    const renderOpeningTimes = (lower: string, upper: string) => {
        const formattedTime = {
            lower: formatPL(parse(lower, "HH:mm:ss", new Date()), "HH:mm"),
            upper: formatPL(parse(upper, "HH:mm:ss", new Date()), "HH:mm")
        };

        return (
            <span>
                {formattedTime.lower} - {formattedTime.upper}
            </span>
        );
    };

    const theme = useTheme();

    const renderSalesOffice = (salesOffice: ISalesOffice) => {
        const isOpenHours =
            salesOffice.open_hours_1 ||
            salesOffice.open_hours_2 ||
            salesOffice.open_hours_3 ||
            salesOffice.open_hours_4 ||
            salesOffice.open_hours_5 ||
            salesOffice.open_hours_6 ||
            salesOffice.open_hours_7;
        const isNote = salesOffice.info_note_1 || salesOffice.info_note_2 || salesOffice.info_note_3;
        const address = salesOffice.address
            .split("\n")
            .map((e) => e.trim())
            .join(" ");

        return (
            <>
                <div css={[salesOfficeInfo]}>
                    <Text align="center" variant="body_copy_1" css={mb(3)}>
                        Adres biura sprzedaży:
                    </Text>

                    <address className="bt">{address}</address>
                </div>

                {isOpenHours && (
                    <>
                        <Text align="center" variant="body_copy_1" css={mb(3)}>
                            Godziny otwarcia biura sprzedaży:
                        </Text>

                        <ul css={[openHoursList]}>
                            {salesOffice.open_hours_1 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Poniedziałek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_1.lower, salesOffice.open_hours_1.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_2 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Wtorek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_2.lower, salesOffice.open_hours_2.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_3 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Środa: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_3.lower, salesOffice.open_hours_3.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_4 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Czwartek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_4.lower, salesOffice.open_hours_4.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_5 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Piątek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_5.lower, salesOffice.open_hours_5.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_6 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Sobota: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_6.lower, salesOffice.open_hours_6.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_7 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Niedziela: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_7.lower, salesOffice.open_hours_7.upper)}
                                    </Text>
                                </li>
                            )}
                        </ul>
                    </>
                )}

                {isNote && (
                    <div css={[noteHolder]}>
                        {salesOffice.info_note_1 && (
                            <Text color={theme.colors.gray[700]} align="center" mb="1.6rem" variant="info_txt_1">
                                {salesOffice.info_note_1}
                            </Text>
                        )}

                        {salesOffice.info_note_2 && (
                            <Text color={theme.colors.gray[700]} align="center" mb="1.6rem" variant="info_txt_1">
                                {salesOffice.info_note_2}
                            </Text>
                        )}

                        {salesOffice.info_note_3 && (
                            <Text color={theme.colors.gray[700]} align="center" mb="1.6rem" variant="info_txt_1">
                                {salesOffice.info_note_3}
                            </Text>
                        )}
                    </div>
                )}

                {!isForSaleOverLimit && (
                    <>
                        {!offer.is_phone_clip_enabled && salesOffice.phone && renderPhoneButton(salesOffice.phone)}

                        {!offer.is_phone_clip_enabled && salesOffice.mobile && renderPhoneButton(salesOffice.mobile)}
                    </>
                )}
            </>
        );
    };

    const renderPhoneButton = (phone: string) => {
        return (
            <div css={mb(3)}>
                <Link href={`tel:+${phone}`}>{phoneNumberFormat(phone)}</Link>
            </div>
        );
    };
    const storeHash = useMemo(() => generateHash(), []);
    const firstSalesOffice = props.offer.sales_offices && props.offer.sales_offices[0];

    return (
        <div css={offerSalesOfficesBase}>
            <div>
                {offer.logo?.o_log_200 ? (
                    <div css={[flexJustifyCenter, mb(3)]}>
                        <img src={offer.logo.o_log_200} alt={`${offer.name} logo`} width={160} height={120} loading="lazy" />
                    </div>
                ) : (
                    <>
                        {vendor.logo?.v_log_160x120 && <img src={vendor.logo.v_log_160x120} alt={`${vendor.name} logo`} width={160} height={120} />}

                        <Text align="center" as="p" variant="headline_5" css={mv(3)} className="bt">
                            {vendor.name}
                        </Text>
                    </>
                )}

                {offer.assistant ? (
                    <>
                        <Text variant="headline_6" as="p" css={mv(0.5)} align="center">
                            {offer.assistant.name}
                        </Text>
                        <Text variant="body_copy_2" as="p" css={[mb(2), mt(0.5)]} align="center">
                            OPIEKUN INWESTYCJI
                        </Text>
                    </>
                ) : null}

                {offer.website && (
                    <Link
                        css={[mb(3), display("inline-block")]}
                        href={
                            offer.configuration.include_utm
                                ? appendQueryString(offer.website, {
                                      utm_campaign: "prezentacja",
                                      utm_medium: "referral",
                                      utm_source: "rynekpierwotny"
                                  })
                                : offer.website
                        }
                        target="_blank"
                        rel="nofollow"
                        data-gtm-click="Odwiedź stronę inwestycji"
                        onClick={() => {
                            if (property) {
                                propertyViewExternalSiteClick(property, offer, offer.vendor);
                            } else {
                                offerViewExternalSiteClick(offer, offer.vendor);
                            }
                        }}
                    >
                        <Highlight>
                            <Text variant="button_medium">Strona internetowa inwestycji</Text>
                        </Highlight>
                    </Link>
                )}

                {firstSalesOffice && renderSalesOffice(firstSalesOffice)}

                {!isForSaleOverLimit && offer.is_phone_clip_enabled && offer.configuration.phone_clip && renderPhoneButton(offer.configuration.phone_clip)}

                {!isForSaleOverLimit && !hideForm && (
                    <div css={flexJustifyCenter}>
                        <ApplicationModalButton
                            storeHash={storeHash}
                            source={ApplicationSource.ApplicationOfferScheduleMeeting}
                            sourceSection={ApplicationSourceSection.BOTTOM}
                            vendorId={props.offer.vendor.id}
                            offerId={props.offer.id}
                            forceClose={getOfferDisplayStatus(props.offer) === "limited"}
                            salesOfficeId={firstSalesOffice && firstSalesOffice.id}
                            render={({openModal}) => (
                                <Button variant="filled_primary" onClick={openModal}>
                                    {InquiryButtonTypeName.ARRANGE_MEETING}
                                </Button>
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const offerSalesOfficesBase = css`
    ${p(3)};
    ${borderRadius(2)};
    background-color: #fff;
    text-align: center;
`;

const salesOfficeInfo = css`
    ${mb(3)};
`;

const noteHolder = css`
    max-width: 50rem;
    margin: 0 auto 2.4rem auto;
`;

const openHoursList = css`
    list-style: none;
    padding-left: 0;
    margin: 0 auto 2.4rem;

    > li {
        padding: 0.2rem 0;
    }
`;
import React, {useMemo} from "react";
import {css, useTheme} from "@emotion/react";
import {parse} from "date-fns";

import {Button} from "@pg-design/button-module";
import {borderRadius, display, flexJustifyCenter, mb, mt, mv, p} from "@pg-design/helpers-css";
import {Link} from "@pg-design/link-module";
import {Highlight, Text} from "@pg-design/text-module";
import {appendQueryString} from "@pg-mono/request";
import {formatPL, phoneNumberFormat} from "@pg-mono/string-utils";

import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {Country} from "../../region/types/Country";
import {offerViewExternalSiteClick, propertyViewExternalSiteClick} from "../../tracking/algolytics_hits/external_site_click";
import {generateHash} from "../../utils/generate_hash";
import {InquiryButtonTypeName} from "../detail/constants/inquiry_button_type";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferAssistant} from "../types/IOfferAssistant";
import {IOfferTrader} from "../types/IOfferTrader";
import {ISalesOffice} from "../types/ISalesOffice";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";

interface IProps {
    offer: {
        assistant: IOfferAssistant | null;
        configuration: {
            include_utm: boolean;
            phone_clip: string | null;
            display_type: OfferDisplayType;
            limited_presentation?: boolean;
            is_autoresponder_enabled: boolean;
        };
        geo_point: {
            coordinates: [number, number];
        };
        id: number;
        is_phone_clip_enabled: boolean;
        logo: {
            o_log_200: string;
        } | null;
        name: string;
        region: {
            country: Country;
            full_name: string;
            id: number;
        };
        sales_offices: ISalesOffice[];
        traders: IOfferTrader[];
        type: number;
        vendor: {
            id: number;
            name: string;
            slug: string;
            logo: {
                v_log_160x120: string;
            } | null;
        };
        website: string | null;
        is_active?: boolean;
    };
    property?: {
        area: number;
        discount: number;
        floor: number;
        floors: number;
        id: number;
        is_luxury: boolean;
        is_reserved: boolean;
        mdm: Object | null;
        number: string;
        plan: string | null;
        rooms: number;
    };
}

export const OfferSalesOffices = (props: IProps) => {
    const {offer, property} = props;
    const {vendor} = props.offer;
    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const hideForm = offer.sales_offices.length === 0;

    const renderOpeningTimes = (lower: string, upper: string) => {
        const formattedTime = {
            lower: formatPL(parse(lower, "HH:mm:ss", new Date()), "HH:mm"),
            upper: formatPL(parse(upper, "HH:mm:ss", new Date()), "HH:mm")
        };

        return (
            <span>
                {formattedTime.lower} - {formattedTime.upper}
            </span>
        );
    };

    const theme = useTheme();

    const renderSalesOffice = (salesOffice: ISalesOffice) => {
        const isOpenHours =
            salesOffice.open_hours_1 ||
            salesOffice.open_hours_2 ||
            salesOffice.open_hours_3 ||
            salesOffice.open_hours_4 ||
            salesOffice.open_hours_5 ||
            salesOffice.open_hours_6 ||
            salesOffice.open_hours_7;
        const isNote = salesOffice.info_note_1 || salesOffice.info_note_2 || salesOffice.info_note_3;
        const address = salesOffice.address
            .split("\n")
            .map((e) => e.trim())
            .join(" ");

        return (
            <>
                <div css={[salesOfficeInfo]}>
                    <Text align="center" variant="body_copy_1" css={mb(3)}>
                        Adres biura sprzedaży:
                    </Text>

                    <address className="bt">{address}</address>
                </div>

                {isOpenHours && (
                    <>
                        <Text align="center" variant="body_copy_1" css={mb(3)}>
                            Godziny otwarcia biura sprzedaży:
                        </Text>

                        <ul css={[openHoursList]}>
                            {salesOffice.open_hours_1 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Poniedziałek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_1.lower, salesOffice.open_hours_1.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_2 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Wtorek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_2.lower, salesOffice.open_hours_2.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_3 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Środa: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_3.lower, salesOffice.open_hours_3.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_4 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Czwartek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_4.lower, salesOffice.open_hours_4.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_5 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Piątek: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_5.lower, salesOffice.open_hours_5.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_6 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Sobota: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_6.lower, salesOffice.open_hours_6.upper)}
                                    </Text>
                                </li>
                            )}

                            {salesOffice.open_hours_7 && (
                                <li>
                                    <Text as="span" variant="body_copy_2">
                                        <span>Niedziela: </span>
                                        {renderOpeningTimes(salesOffice.open_hours_7.lower, salesOffice.open_hours_7.upper)}
                                    </Text>
                                </li>
                            )}
                        </ul>
                    </>
                )}

                {isNote && (
                    <div css={[noteHolder]}>
                        {salesOffice.info_note_1 && (
                            <Text color={theme.colors.gray[700]} align="center" mb="1.6rem" variant="info_txt_1">
                                {salesOffice.info_note_1}
                            </Text>
                        )}

                        {salesOffice.info_note_2 && (
                            <Text color={theme.colors.gray[700]} align="center" mb="1.6rem" variant="info_txt_1">
                                {salesOffice.info_note_2}
                            </Text>
                        )}

                        {salesOffice.info_note_3 && (
                            <Text color={theme.colors.gray[700]} align="center" mb="1.6rem" variant="info_txt_1">
                                {salesOffice.info_note_3}
                            </Text>
                        )}
                    </div>
                )}

                {!isForSaleOverLimit && (
                    <>
                        {!offer.is_phone_clip_enabled && salesOffice.phone && renderPhoneButton(salesOffice.phone)}

                        {!offer.is_phone_clip_enabled && salesOffice.mobile && renderPhoneButton(salesOffice.mobile)}
                    </>
                )}
            </>
        );
    };

    const renderPhoneButton = (phone: string) => {
        return (
            <div css={mb(3)}>
                <Link href={`tel:+${phone}`}>{phoneNumberFormat(phone)}</Link>
            </div>
        );
    };
    const storeHash = useMemo(() => generateHash(), []);
    const firstSalesOffice = props.offer.sales_offices && props.offer.sales_offices[0];

    return (
        <div css={offerSalesOfficesBase}>
            <div>
                {offer.logo?.o_log_200 ? (
                    <div css={[flexJustifyCenter, mb(3)]}>
                        <img src={offer.logo.o_log_200} alt={`${offer.name} logo`} width={160} height={120} loading="lazy" />
                    </div>
                ) : (
                    <>
                        {vendor.logo?.v_log_160x120 && <img src={vendor.logo.v_log_160x120} alt={`${vendor.name} logo`} width={160} height={120} />}

                        <Text align="center" as="p" variant="headline_5" css={mv(3)} className="bt">
                            {vendor.name}
                        </Text>
                    </>
                )}

                {offer.assistant ? (
                    <>
                        <Text variant="headline_6" as="p" css={mv(0.5)} align="center">
                            {offer.assistant.name}
                        </Text>
                        <Text variant="body_copy_2" as="p" css={[mb(2), mt(0.5)]} align="center">
                            OPIEKUN INWESTYCJI
                        </Text>
                    </>
                ) : null}

                {offer.website && (
                    <Link
                        css={[mb(3), display("inline-block")]}
                        href={
                            offer.configuration.include_utm
                                ? appendQueryString(offer.website, {
                                      utm_campaign: "prezentacja",
                                      utm_medium: "referral",
                                      utm_source: "rynekpierwotny"
                                  })
                                : offer.website
                        }
                        target="_blank"
                        rel="nofollow"
                        data-gtm-click="Odwiedź stronę inwestycji"
                        onClick={() => {
                            if (property) {
                                propertyViewExternalSiteClick(property, offer, offer.vendor);
                            } else {
                                offerViewExternalSiteClick(offer, offer.vendor);
                            }
                        }}
                    >
                        <Highlight>
                            <Text variant="button_medium">Strona internetowa inwestycji</Text>
                        </Highlight>
                    </Link>
                )}

                {firstSalesOffice && renderSalesOffice(firstSalesOffice)}

                {!isForSaleOverLimit && offer.is_phone_clip_enabled && offer.configuration.phone_clip && renderPhoneButton(offer.configuration.phone_clip)}

                {!isForSaleOverLimit && !hideForm && (
                    <div css={flexJustifyCenter}>
                        <ApplicationModalButton
                            storeHash={storeHash}
                            source={ApplicationSource.ApplicationOfferScheduleMeeting}
                            sourceSection={ApplicationSourceSection.BOTTOM}
                            vendorId={props.offer.vendor.id}
                            offerId={props.offer.id}
                            forceClose={getOfferDisplayStatus(props.offer) === "limited"}
                            salesOfficeId={firstSalesOffice && firstSalesOffice.id}
                            render={({openModal}) => (
                                <Button variant="filled_primary" onClick={openModal}>
                                    {InquiryButtonTypeName.ARRANGE_MEETING}
                                </Button>
                            )}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const offerSalesOfficesBase = css`
    ${p(3)};
    ${borderRadius(2)};
    background-color: #fff;
    text-align: center;
`;

const salesOfficeInfo = css`
    ${mb(3)};
`;

const noteHolder = css`
    max-width: 50rem;
    margin: 0 auto 2.4rem auto;
`;

const openHoursList = css`
    list-style: none;
    padding-left: 0;
    margin: 0 auto 2.4rem;

    > li {
        padding: 0.2rem 0;
    }
`;
